import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactViewDetailDto, SaveContactViewRequest } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'

/**
 * The saved views above the contacts board.
 *
 * **A view is not a segment.** A view is a way of looking at this list — its
 * rules, its order, the columns it hides. A segment is an audience a campaign
 * is sent to, and has members as well as rules. These were the same thing, so
 * saving a way of looking at the board created something a campaign could be
 * pointed at, and the three an organisation starts with turned up in the
 * segment list as though somebody had built them. Segments are made on the
 * Segments screen.
 *
 * What the two still share is the rule vocabulary: a view's filter is written
 * with the same rules and translated by the same code, because "who matches
 * these rules" has to have one answer.
 *
 * Loaded separately from the rows, and a failure leaves the board working with
 * no tabs above it. That is the board as it was before saved views existed — a
 * worse board, not a broken one, and taking the contact list down because the
 * view list did not answer would be the wrong trade.
 */

export function useContactViews({ client }: { readonly client: ApiClient }) {
  const [views, setViews] = useState<readonly ContactViewDetailDto[]>([])

  const load = useCallback(async () => {
    try {
      setViews((await client.contactViews()).views)
    } catch {
      setViews([])
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Saves a view, or replaces one whole when `viewId` is given.
   *
   * Replacing rather than patching is what renaming goes through: a view is its
   * name and its rules together, and two writes would be a window where the tab
   * says one thing and the rows show another.
   */
  const save = useCallback(
    async (view: SaveContactViewRequest, viewId?: string) => {
      const result = await client.saveContactView(view, viewId)
      if (result.status === 'saved') await load()
      return result
    },
    [client, load],
  )

  const remove = useCallback(
    async (viewId: string) => {
      await client.removeContactView(viewId)
      await load()
    },
    [client, load],
  )

  return { views, save, remove, reload: load }
}
